"use client";
import Image from "next/image";
import {
  DURATION_OPTIONS,
  type CatalogFiltersController,
} from "@/hooks/useCatalogFilters";

interface Props {
  /** Filter state + handlers from `useCatalogFilters`. */
  controller: CatalogFiltersController;
  /** Wrapper classes — lets each host keep its own separator/spacing. */
  className?: string;
  /** Radio `name`, unique per host so the two mounted copies stay independent. */
  groupName?: string;
}

/**
 * "Durée" filter section — collapsible list of day buckets plus a custom
 * period backed by the formations' session dates. Shared by the desktop
 * CatalogFilterSidebar and the mobile FilterDrawer so both stay in sync.
 */
export default function DurationFilter({
  controller,
  className = "",
  groupName = "duration",
}: Props) {
  const {
    selectedDuration,
    setSelectedDuration,
    durationDateFrom,
    setDurationDateFrom,
    durationDateTo,
    setDurationDateTo,
    isDurationOpen,
    setIsDurationOpen,
  } = controller;

  return (
    <div className={className}>
      <div
        className={`flex items-center justify-between gap-4 ${isDurationOpen ? "mb-4" : ""}`}
      >
        <p className="text-[15px] font-bold leading-4.5 hanken-grotesk tracking-[0.2px]">
          Durée
        </p>
        <button
          type="button"
          aria-label={isDurationOpen ? "reduce" : "expand"}
          onClick={() => setIsDurationOpen((v) => !v)}
          className="cursor-pointer w-4 h-4"
        >
          <Image
            className="w-4 shrink-0"
            src={
              isDurationOpen
                ? "/assets/icons/minus.svg"
                : "/assets/icons/plus.svg"
            }
            alt={isDurationOpen ? "minus" : "plus"}
            width={16}
            height={16}
          />
        </button>
      </div>

      {isDurationOpen && (
        <div className="flex flex-col gap-3.5">
          {DURATION_OPTIONS.map(({ value, label }) => (
            <div key={value}>
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="radio"
                  name={groupName}
                  value={value}
                  checked={selectedDuration === value}
                  onClick={() =>
                    setSelectedDuration(
                      selectedDuration === value ? null : value,
                    )
                  }
                  onChange={() => {}}
                  className="w-3.5 h-3.5 cursor-pointer accent-[#1B365E] shrink-0"
                />
                <span
                  className={`text-sm leading-5 ${value === "custom" ? "text-[#1B365E] font-medium" : "text-[#1D1D1D]"}`}
                >
                  {label}
                </span>
              </label>

              {value === "custom" && selectedDuration === "custom" && (
                <div className="mt-3 ml-6 flex items-end gap-2">
                  <div className="flex flex-col gap-1 flex-1">
                    <span className="text-[10px] text-[#ABABAB]">
                      entre le :
                    </span>
                    <div className="flex items-center gap-1.5 border border-[#EFEFEF] rounded px-2 py-1.5 focus-within:border-[#1B365E] transition-colors">
                      <svg
                        className="w-3 h-3 text-[#FC7F16] shrink-0"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                      >
                        <path d="M5 0a1 1 0 0 1 1 1v1h4V1a1 1 0 1 1 2 0v1h1a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h1V1a1 1 0 0 1 1-1zm-2 5v8h10V5H3z" />
                      </svg>
                      <input
                        type="date"
                        value={durationDateFrom}
                        max={durationDateTo || undefined}
                        onChange={(e) => setDurationDateFrom(e.target.value)}
                        className="w-full text-[11px] text-[#1D1D1D] bg-transparent outline-none"
                      />
                    </div>
                  </div>
                  <svg
                    className="w-4 h-3 text-[#FC7F16] shrink-0 mb-2"
                    viewBox="0 0 16 8"
                    fill="none"
                  >
                    <path
                      d="M0 4H14M14 4L10.5 1M14 4L10.5 7"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex flex-col gap-1 flex-1">
                    <span className="text-[10px] text-[#ABABAB]">et le :</span>
                    <div className="flex items-center gap-1.5 border border-[#EFEFEF] rounded px-2 py-1.5 focus-within:border-[#1B365E] transition-colors">
                      <svg
                        className="w-3 h-3 text-[#FC7F16] shrink-0"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                      >
                        <path d="M5 0a1 1 0 0 1 1 1v1h4V1a1 1 0 1 1 2 0v1h1a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h1V1a1 1 0 0 1 1-1zm-2 5v8h10V5H3z" />
                      </svg>
                      <input
                        type="date"
                        value={durationDateTo}
                        min={durationDateFrom || undefined}
                        onChange={(e) => setDurationDateTo(e.target.value)}
                        className="w-full text-[11px] text-[#1D1D1D] bg-transparent outline-none"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
